import React, { useState } from "react";
import api, { CLP, IVA_RATE, INVOICE_SOURCES, formatApiErrorDetail } from "@/lib/api";
import { Field, inputCls, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import CustomerSelect from "@/components/CustomerSelect";
import { X } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal reutilizable para crear una factura. Puede pre-llenarse con un cliente.
 * Props:
 *  - defaultCustomerId (opcional): pre-selecciona un cliente.
 *  - onClose: callback al cerrar.
 *  - onSaved: callback tras guardar exitosamente (recibe la factura creada).
 */
export default function InvoiceForm({
  defaultCustomerId,
  defaultConcept = "",
  defaultNetAmount = 0,
  defaultSource = "manual",
  defaultSourceId = null,
  onClose,
  onSaved,
}) {
  const [data, setData] = useState({
    customer_id: defaultCustomerId || "",
    source: defaultSource,
    source_id: defaultSourceId,
    concept: defaultConcept,
    net_amount: defaultNetAmount,
    status: "por_facturar",
    invoice_number: "",
    issue_date: "",
    paid_date: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const net = Number(data.net_amount || 0);
  const iva = Math.round(net * IVA_RATE);
  const gross = net + iva;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...data, net_amount: Number(data.net_amount) };
      if (!payload.source_id) delete payload.source_id;
      const { data: created } = await api.post("/invoices", payload);
      toast.success("Factura creada");
      onSaved && onSaved(created);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl border-t-4 border-[#FF5A00]" data-testid="invoice-new-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Cuentas por cobrar</div>
            <div className="font-display font-bold text-xl">Nueva factura</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field label="Cliente *">
              <CustomerSelect
                value={data.customer_id}
                onChange={(id) => setData({ ...data, customer_id: id })}
                required
                testid="invoice-customer-select"
              />
            </Field>
          </div>
          <Field label="Fuente">
            <select value={data.source} onChange={(e) => setData({ ...data, source: e.target.value })} className={inputCls}>
              {INVOICE_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Estado inicial">
            <select value={data.status} onChange={(e) => setData({ ...data, status: e.target.value })} className={inputCls} data-testid="invoice-status-select">
              <option value="por_facturar">Por Facturar</option>
              <option value="facturado">Facturado</option>
              <option value="pagado">Pagado</option>
            </select>
          </Field>
          <div className="md:col-span-2">
            <Field label="Concepto *">
              <input required data-testid="invoice-concept-input" value={data.concept} onChange={(e) => setData({ ...data, concept: e.target.value })} className={inputCls} />
            </Field>
          </div>
          <Field label="Monto NETO (CLP) *" hint="El IVA (19%) se agrega automáticamente">
            <input type="number" min="1" required data-testid="invoice-net-input" value={data.net_amount} onChange={(e) => setData({ ...data, net_amount: e.target.value })} className={inputCls} />
          </Field>
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">Cálculo automático</div>
            <div className="px-3 py-2 border border-neutral-200 bg-neutral-50 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-neutral-500">Neto</span><span className="font-mono-kpi">{CLP(net)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">IVA (19%)</span><span className="font-mono-kpi">{CLP(iva)}</span></div>
              <div className="flex justify-between pt-1 border-t border-neutral-200"><span className="font-bold">Total</span><span className="font-mono-kpi font-bold text-[#FF5A00]" data-testid="invoice-gross-preview">{CLP(gross)}</span></div>
            </div>
          </div>
          {(data.status === "facturado" || data.status === "pagado") && (
            <>
              <Field label="N° Factura *">
                <input required data-testid="invoice-number-input" value={data.invoice_number} onChange={(e) => setData({ ...data, invoice_number: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Fecha emisión *">
                <input type="date" required data-testid="invoice-issue-date-input" value={data.issue_date} onChange={(e) => setData({ ...data, issue_date: e.target.value })} className={inputCls} />
              </Field>
            </>
          )}
          {data.status === "pagado" && (
            <div className="md:col-span-2">
              <Field label="Fecha de pago *" hint="Ingresa la fecha real del pago (para migración histórica usa la fecha original)">
                <input type="date" required data-testid="invoice-paid-date-input" value={data.paid_date} onChange={(e) => setData({ ...data, paid_date: e.target.value })} className={inputCls} />
              </Field>
            </div>
          )}
          <div className="md:col-span-2">
            <Field label="Notas">
              <textarea rows={2} value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })} className={inputCls} />
            </Field>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="invoice-save-btn">{saving ? "Guardando…" : "Guardar"}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}
